import { isAndroidApp } from '../appContext';

class BackendService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL ||
                   (process.env.NODE_ENV === 'production'
                     ? 'https://backend-production-65818.up.railway.app'
                     : 'http://localhost:8080');
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    // Only declare Content-Type when there's actually a body to describe.
    // Setting it for body-less requests trips Fastify's FST_ERR_CTP_EMPTY_JSON_BODY
    // and rejects the request before the route handler runs.
    const hasBody = options.body !== undefined;
    const defaultOptions = {
      ...options,
      headers: {
        ...(hasBody && { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    };


    try {
      const response = await fetch(url, defaultOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (process.env.NODE_ENV === 'development') {
          console.error('Backend Response Error:', { status: response.status, errorData });
        }

        // Include validation details if available
        let errorMessage = errorData.error || errorData.message || 'Unknown error';
        if (errorData.details && Array.isArray(errorData.details)) {
          errorMessage += ': ' + errorData.details.join(', ');
        }

        const error = new Error(`Backend API Error: ${response.status} - ${errorMessage}`);
        error.status = response.status;
        error.details = errorData.details;
        throw error;
      }

      const jsonResponse = await response.json();
      
      
      return jsonResponse;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Backend Service Error:', error);
      }
      throw error;
    }
  }

  async signupUser(userData, idToken) {
    return this.makeRequest('/users/firebase-signup', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(userData),
    });
  }

  async updateLastLogin(uid, idToken) {
    return this.makeRequest('/users/last-login', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ uid }),
    });
  }

  async checkHealth() {
    return this.makeRequest('/health');
  }

  async getUserEmailPreferences(idToken) {
    return this.makeRequest('/users/email-preferences', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async updateUserEmailPreferences(preferences, idToken) {
    return this.makeRequest('/users/email-preferences', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(preferences),
    });
  }

  async changePublicUsername(newPublicUsername, idToken) {
    return this.makeRequest('/users/public-username', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ newPublicUsername }),
    });
  }

  async updateUserCountry(countryCode, idToken) {
    return this.makeRequest('/users/country', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ countryCode }),
    });
  }

  async deleteUser(uid, idToken) {
    return this.makeRequest(`/users/${uid}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  /**
   * Report that a review session just finished. Returns when the learner's next
   * batch unlocks so the completion screen can show it, and lets the backend
   * schedule the first-day follow-up email.
   */
  async reportSessionComplete(idToken) {
    return this.makeRequest('/user-progress/session-complete', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async markOnboardingComplete(idToken) {
    return this.makeRequest('/users/onboarding-complete', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
  }

  // { available, reason }: whether onboarding should offer the level picker.
  async getDropInStatus(idToken) {
    return this.makeRequest('/user-progress/drop-in', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // One-time: start the curriculum at a later level (onboarding drop-in).
  async dropInAtLevel(level, idToken) {
    return this.makeRequest('/user-progress/drop-in', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ level }),
    });
  }

  // One-time, from My Profile: move to a level (the account's one level change).
  async restartAtLevel(level, idToken) {
    return this.makeRequest('/user-progress/restart-at-level', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ level }),
    });
  }

  async markOnboardingStarted(idToken) {
    return this.makeRequest('/users/onboarding-started', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
  }

  async markOnboardingSkipped(idToken) {
    return this.makeRequest('/users/onboarding-skipped', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
  }

  async exportKnownKanji(uid, idToken) {
    return this.makeRequest(`/users/${uid}/export-known-kanji`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // Kanji API methods
  async getKanjiDetails(kanji, idToken, source = 'unknown') {
    const headers = {
      'X-Request-Source': source,
    };
    // Only include Authorization header if token is provided (public access support)
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    return this.makeRequest(`/kanji/${encodeURIComponent(kanji)}`, {
      method: 'GET',
      headers,
    });
  }

  async getKanjiNavigation(kanji, limit = 10, idToken) {
    const headers = {};
    // Only include Authorization header if token is provided (public access support)
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    return this.makeRequest(`/kanji/${encodeURIComponent(kanji)}/navigation?limit=${limit}`, {
      method: 'GET',
      headers,
    });
  }

  async searchKanji(query, limit = 20, idToken) {
    const headers = {};
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    return this.makeRequest(`/kanji/search?q=${encodeURIComponent(query)}&limit=${limit}`, {
      method: 'GET',
      headers,
    });
  }

  async getKanjiByLevels(levels, idToken) {
    const levelsParam = Array.isArray(levels) ? levels.join(',') : levels;
    const headers = {};
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    return this.makeRequest(`/kanji/levels?levels=${levelsParam}`, {
      method: 'GET',
      headers,
    });
  }

  async getMaxLevel(idToken) {
    const headers = {};
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    return this.makeRequest('/kanji/max-level', {
      method: 'GET',
      headers,
    });
  }

  // User Progress API methods
  async saveUserReview(reviewData, idToken) {
    return this.makeRequest('/user-progress/save-review', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(reviewData),
    });
  }

  // When the next reviews unlock and how many days the learner has studied.
  async getNextReview(idToken) {
    return this.makeRequest('/user-progress/next-review', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // Record a feature use the product doesn't otherwise store (e.g. Extra Study),
  // for the admin Cohorts tab. Fire-and-forget; never let it break the page.
  async logActivity(idToken, activity, data = {}, { keepalive = false } = {}) {
    try {
      return await this.makeRequest('/user-progress/activity', {
        method: 'POST',
        keepalive,
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ activity, data }),
      });
    } catch (e) {
      return { recorded: false };
    }
  }

  // Streak days follow the learner's own calendar day: report the browser's
  // IANA timezone (no permission needed). Fire-and-forget.
  async reportTimeZone(idToken, timeZone) {
    try {
      return await this.makeRequest('/user-progress/timezone', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ timeZone }),
      });
    } catch (e) {
      return { saved: false };
    }
  }

  // Local mock stack only (REACT_APP_MOCK_MODE): pretend `hours` have passed.
  async devFastForward(idToken, hours = 2) {
    return this.makeRequest('/dev/fast-forward', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ hours }),
    });
  }

  async saveUserNewLessonProgress(progressData, idToken) {
    return this.makeRequest('/user-progress/save-new-lesson-progress', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(progressData),
    });
  }

  // Quiz API methods
  async reportQuizIssue(message, context, page, idToken) {
    return this.makeRequest('/quiz/report-issue', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        message,
        context,
        page
      }),
    });
  }

  async challengeAnswer(questionData, userInput, correctAnswer, idToken) {
    return this.makeRequest('/quiz/challenge-answer', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        questionData,
        userInput,
        correctAnswer
      }),
    });
  }

  async setupCustomQuiz(kanjiList, excludedQuestionTypes = [], questionStyle = 'both', collectionPath, idToken) {
    return this.makeRequest('/quiz/setup-custom', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        kanjiList,
        excludedQuestionTypes,
        questionStyle,
        collectionPath
      }),
    });
  }

  // Reader AI API methods
  async analyzeSentence(sentence, idToken) {
    return this.makeRequest('/reader/sentence-analysis', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        sentence
      }),
    });
  }

  async lookupVocab(word, idToken) {
    return this.makeRequest('/reader/vocab-lookup', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        word
      }),
    });
  }

  async lookupKanji(kanji, idToken) {
    return this.makeRequest('/reader/kanji-lookup', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        kanji
      }),
    });
  }

  // Leaderboard Message API methods
  async createLeaderboardMessage(message, messageType = 'leaderboard', idToken) {
    return this.makeRequest('/users/leaderboard-message', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        message,
        messageType
      }),
    });
  }

  async getUserLeaderboardMessage(idToken) {
    return this.makeRequest('/users/leaderboard-message', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async updateLeaderboardMessage(updates, idToken) {
    return this.makeRequest('/users/leaderboard-message', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(updates),
    });
  }

  async deleteLeaderboardMessage(idToken) {
    return this.makeRequest('/users/leaderboard-message', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getAllLeaderboardMessages(usernames = null) {
    const url = usernames 
      ? `/leaderboard/messages?usernames=${encodeURIComponent(usernames.join(','))}`
      : '/leaderboard/messages';
    
    return this.makeRequest(url, {
      method: 'GET',
    });
  }

  async voteOnLeaderboardMessage(publicUsername, vote, idToken) {
    return this.makeRequest(`/leaderboard/messages/${encodeURIComponent(publicUsername)}/vote`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        vote
      }),
    });
  }

  // App Home Load API methods
  async getLessonCount(uid, idToken) {
    return this.makeRequest(`/users/${uid}/lesson-count`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getLessonKanji(uid, idToken, limit = 4) {
    return this.makeRequest(`/users/${uid}/lesson-kanji?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getAppHomeStats(uid, idToken) {
    return this.makeRequest(`/users/${uid}/app-home-stats`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async loadLessonQuestions(kanjiList, idToken) {
    return this.makeRequest('/lesson-questions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        kanjiList
      }),
    });
  }

  // Review API methods
  async getReviewCount(uid, idToken) {
    return this.makeRequest(`/users/${uid}/review-count`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getKanjiToReview(uid, idToken) {
    return this.makeRequest(`/users/${uid}/kanji-to-review`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getReviewBuckets(uid, idToken) {
    return this.makeRequest(`/users/${uid}/review-buckets`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // Leaderboard API methods
  async getAllLeaderboards(limit = 5, idToken) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    
    const url = `/leaderboard/all${params.toString() ? '?' + params.toString() : ''}`;
    
    return this.makeRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // No auth: the public /leaderboards page (entries come without uids).
  async getPublicLeaderboards(limit = 100) {
    return this.makeRequest(`/leaderboard/public?limit=${limit}`, { method: 'GET' });
  }

  // The signed-in learner's rank and next target on each board.
  async getLeaderboardStanding(idToken) {
    return this.makeRequest('/leaderboard/me/standing', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getUserLeaderboardPositions(uid, idToken) {
    return this.makeRequest(`/leaderboard/user/${uid}/positions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // User Profile API methods
  async getUserProfile(idToken) {
    return this.makeRequest('/users/profile', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getUserSubscription(uid, idToken) {
    return this.makeRequest(`/users/${uid}/subscription`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // User Progress API methods
  async getUserProgress(uid, idToken) {
    return this.makeRequest(`/users/${uid}/progress`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // User Stats API methods (replaces Firebase USER_STATS_FUNCTIONS.js)
  async getUserStats(uid, idToken) {
    return this.makeRequest(`/users/${uid}/stats`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getKanjiMastery(uid, idToken) {
    return this.makeRequest(`/users/${uid}/kanji-mastery`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // Level Up API method (replaces Firebase HANDLE_LEVEL_UP.js)
  async handleLevelUp(idToken) {
    return this.makeRequest('/user-progress/level-up', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
  }

  // Immersion API methods
  async checkContentOwnership(contentId, chapterNumber, generatePresignedUrls, idToken) {
    const params = new URLSearchParams();
    params.append('contentId', contentId);
    if (chapterNumber !== undefined && chapterNumber !== null) {
      params.append('chapterNumber', chapterNumber.toString());
    }
    if (generatePresignedUrls !== undefined) {
      params.append('generatePresignedUrls', generatePresignedUrls.toString());
    }

    return this.makeRequest(`/immersion/content/check-ownership?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async purchaseStreakFreeze(idToken, quantity = 1) {
    return this.makeRequest('/user-progress/purchase-streak-freeze', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ quantity }),
    });
  }

  // Extra Study API methods
  async getKanjiByStatus(statuses, idToken) {
    const statusesParam = Array.isArray(statuses) ? statuses.join(',') : statuses;
    return this.makeRequest(`/extra-study/kanji-by-status?statuses=${encodeURIComponent(statusesParam)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getCriticalConditionKanji(idToken) {
    return this.makeRequest('/extra-study/critical-condition', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getKanjiByJLPT(levels, idToken) {
    const levelsParam = Array.isArray(levels) ? levels.join(',') : levels;
    return this.makeRequest(`/extra-study/kanji-by-jlpt?levels=${encodeURIComponent(levelsParam)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  async getMangaPages(contentId, chapterNumber, idToken) {
    const params = new URLSearchParams();
    params.append('contentId', contentId);
    params.append('chapterNumber', chapterNumber.toString());

    return this.makeRequest(`/immersion/manga/pages?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }

  // Auth API methods (magic link)
  async requestMagicLink(email, attributionVisitId) {
    const body = { email };
    if (attributionVisitId) body.attributionVisitId = attributionVisitId;
    const result = await this.makeRequest('/auth/magic-link/request', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    // Local mock stack only: there is no inbox, so the backend returns the code
    // and the mock toolbar shows it.
    if (process.env.REACT_APP_MOCK_MODE === '1' && result?.devCode) {
      try { sessionStorage.setItem('mockDevCode', result.devCode); } catch (e) { /* ignore */ }
      window.dispatchEvent(new CustomEvent('mock-dev-code', { detail: result.devCode }));
    }
    return result;
  }

  async verifyMagicLink({ token, email, code, country }) {
    // Forward the attribution visit id (if any) so the backend can record the
    // signup conversion server-side at the moment the account is created.
    const attributionVisitId = (() => {
      try { return localStorage.getItem('attribution_visit_id') || undefined; }
      catch { return undefined; }
    })();
    return this.makeRequest('/auth/magic-link/verify', {
      method: 'POST',
      body: JSON.stringify({ token, email, code, country, attributionVisitId }),
    });
  }

  // Sign in / sign up with a Google ID token (Google Identity Services).
  async signInWithGoogle(credential, country) {
    const attributionVisitId = (() => {
      try { return localStorage.getItem('attribution_visit_id') || undefined; }
      catch { return undefined; }
    })();
    return this.makeRequest('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential, country: country || undefined, attributionVisitId }),
    });
  }

  async createSubscriptionCheckout(priceId, successUrl, cancelUrl, idToken) {
    // Backstop: Google Play doesn't allow Stripe checkout inside the Android app.
    if (isAndroidApp()) throw new Error('Purchases are not available in the Android app');
    return this.makeRequest('/payment/create-subscription-checkout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ priceId, successUrl, cancelUrl }),
    });
  }

  async createPortalSession(returnUrl, idToken) {
    return this.makeRequest('/payment/create-portal-session', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ returnUrl }),
    });
  }

  async logout(idToken) {
    return this.makeRequest('/auth/logout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
  }
}

export const backendService = new BackendService();